import { useEffect, useRef, useState, useCallback } from "react";
import { hapticSelection } from "../services/haptics";

/**
 * Pull-to-refresh hook.
 * @param {Function} onRefresh - async function to call on pull. Should return a promise.
 * @param {Object} options
 * @param {number} options.threshold - pull distance in px to trigger (default 80)
 * @returns {{ isRefreshing: boolean, pullDistance: number, containerRef: React.Ref }}
 */
export default function usePullToRefresh(onRefresh, { threshold = 80 } = {}) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const pullDistanceRef = useRef(0);
  const startY = useRef(0);
  const pulling = useRef(false);
  const containerRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    const el = containerRef.current;
    if (!el || el.scrollTop > 0) return;
    startY.current = e.touches[0].clientY;
    pulling.current = true;
  }, []);

  const hadCrossed = useRef(false);

  const handleTouchMove = useCallback((e) => {
    if (!pulling.current) return;
    const diff = e.touches[0].clientY - startY.current;
    if (diff > 0) {
      const newDistance = Math.min(diff * 0.5, threshold * 1.5);
      if (newDistance >= threshold && !hadCrossed.current) {
        hadCrossed.current = true;
        hapticSelection();
      }
      pullDistanceRef.current = newDistance;
      setPullDistance(newDistance);
    }
  }, [threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (!pulling.current) return;
    pulling.current = false;
    hadCrossed.current = false;

    if (pullDistanceRef.current >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
    pullDistanceRef.current = 0;
    setPullDistance(0);
  }, [threshold, isRefreshing, onRefresh]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: true });
    el.addEventListener("touchend", handleTouchEnd);

    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return { isRefreshing, pullDistance, containerRef };
}
